import React, {FunctionComponent, useState, ReactElement, useRef} from 'react';
import {Container, _ReactPixi} from '@inlet/react-pixi';
import {IHitArea, Container as PixiContainer} from 'pixi.js';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

export type ButtonCustomProps = _ReactPixi.IContainer & {
    disabled?: boolean;
    active?: boolean;
    hitArea?: IHitArea | null;
    press: null | (() => void);
};

export const ButtonCustomIdle: FunctionComponent<{}> = (props) => {
    return <>{props.children}</>;
};

export const ButtonCustomPressed: FunctionComponent<{}> = (props) => {
    return <>{props.children}</>;
};

export const ButtonCustomHover: FunctionComponent<{}> = (props) => {
    return <>{props.children}</>;
};

export const ButtonCustomDisabled: FunctionComponent<{}> = (props) => {
    return <>{props.children}</>;
};

function findComponent(children: React.ReactNode = [], component: Function): React.ReactNode | null {
    if (!Array.isArray(children)) {
        children = [children];
    }

    return (children as ReactElement[])
        .find(item => item.type === component) || null;
}

enum State {
    Idle,
    Over,
    Press,
    Disable,
    None
}

const ButtonCustomComponent: FunctionComponent<ButtonCustomProps> = (props) => {
    let state = State.None;

    const btnRef = useRef<PixiContainer|null>(null);
    const [pointerover, setPointerover] = useState(false);
    const [pointerdown, setPointerdown] = useState(false);

    const buttonCustomDisabled = findComponent(props.children, ButtonCustomDisabled);
    const buttonCustomHover = findComponent(props.children, ButtonCustomHover);
    const buttonCustomPressed = findComponent(props.children, ButtonCustomPressed);
    const buttonCustomIdle = findComponent(props.children, ButtonCustomIdle);

    if (props.disabled && buttonCustomDisabled) {
        state = State.Disable;
    } else if ((pointerdown || props.active) && buttonCustomPressed) {
        state = State.Press;
    } else if (pointerover && buttonCustomHover) {
        state = State.Over;
    } else if (buttonCustomIdle) {
        state = State.Idle;
    }

    if (state === State.None) {
        throw new Error('MenuBarButton state is missing!\nexample <ButtonCustom><ButtonCustomIdle> ... </ButtonCustomIdle></ButtonCustom>');
    }

    if (btnRef.current) {
        (btnRef.current as any)[AUTOMATION_META] = () => {
            const automationMeta: AutomationMeta = {
                type: 'button',
                disabled: !!props.disabled,
                over: pointerover,
                pressed: pointerdown
            };

            return automationMeta;
        };
    }

    return <Container
        name='ButtonCustom'
        {...props}
        ref={btnRef}
        interactive={true}
        buttonMode={!props.disabled}
        hitArea={props.hitArea || null!}

        pointerover={ _ => setPointerover(true)}
        pointerout={ _ => setPointerover(false)}

        pointerdown={ _ => setPointerdown(true)}
        pointerup={ _ => setPointerdown(false)}
        pointerupoutside={ _ => setPointerdown(false)}

        pointertap={props.press as () => void}
    >
        <Container name='Idle' visible={state === State.Idle}>{buttonCustomIdle}</Container>
        <Container name='Disable' visible={state === State.Disable}>{buttonCustomDisabled}</Container>
        <Container name='Over' visible={state === State.Over}>{buttonCustomHover}</Container>
        <Container name='Press' visible={state === State.Press}>{buttonCustomPressed}</Container>
    </Container>;
};

export const ButtonCustom = Object.assign(ButtonCustomComponent, {
    Idle: ButtonCustomIdle,
    Pressed: ButtonCustomPressed,
    Hover: ButtonCustomHover,
    Disabled: ButtonCustomDisabled
});
